import { type VMSCameraId } from '@solinkcorp/api-types';
import { Button } from '@solinkcorp/solink-components';
import { type IEvent } from 'clients/PlatformApi';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import { type CameraIdentifier } from 'components/VideoPlayer/controller/PlayingCamerasController';
import React, { useContext } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { AuthStore } from 'stores/AuthStore';
import { LocationStore } from 'stores/LocationStore';
import * as EventUtil from 'utils/EventUtil';
import EventQuickViewer from '../EventQuickViewer';
import Scroller from '../Scroller/Scroller';

interface EventThumbnailsProps {
  event: IEvent;
  active?: boolean;
  /** Called with a vmsCameraId, or undefined to play all cameras. */
  onPlayEvent: (vmsCameraId?: string) => void;
  playingCameraIds: readonly CameraIdentifier[];
  width: number;
  isVideoPlayerOpen?: boolean;
}

const EventThumbnails: React.FC<EventThumbnailsProps> = ({
  event,
  active,
  onPlayEvent,
  playingCameraIds,
  width,
  isVideoPlayerOpen,
}) => {
  const locale = useLocale();
  const tenantId = useFluxStore(AuthStore, (store) => store.getUser()?.app_metadata.tenantId);
  const camerasDictionary = useFluxStore(LocationStore, (store) =>
    store.getAllLocationCamerasDict(),
  );
  const { masterLocationsList } = useContext(CommonContext);

  let initIndex: number | undefined;
  const { cameras: eventCameras, source: eventCamerasSource } =
    EventUtil.getCamerasForThumbnails(event);
  const quickViewItems = eventCameras.map((cam, idx) => {
    if (
      isVideoPlayerOpen &&
      playingCameraIds &&
      initIndex === null &&
      playingCameraIds.some(({ vmsCameraId }) => vmsCameraId === cam.id)
    ) {
      initIndex = idx;
    }

    return (
      <EventQuickViewer
        key={`event-quick-view-${idx}`}
        event={event}
        camIdx={idx}
        images={cam.images}
        bucketPrefix={tenantId}
        active={active}
        onPlayEvent={onPlayEvent}
        width={width}
        eventCamerasLength={eventCameras.length}
        camerasDictionary={camerasDictionary}
        locale={locale}
        masterLocationsList={masterLocationsList}
        targetCamera={{
          eventCamera: cam,
          vmsCameraId: cam.id as VMSCameraId,
          warning: undefined,
          callhomeCamera: camerasDictionary[cam.chId ?? ''],
        }}
      />
    );
  });

  const actions = [];
  if (eventCamerasSource !== 'all') {
    actions.push(
      <div className="camera-source-button" key="camera-source-button">
        <Button
          isPrimary={false}
          colorMode={'light' as any}
          disabled={true}
          size="small"
          text={locale.videoPlayerOptions.camerasSource[eventCamerasSource]}
          tooltip={locale.videoPlayerOptions.camerasSourceTooltip[eventCamerasSource]}
        />
      </div>,
    );
  }

  return EventUtil.hasCameras(event) ? (
    <Scroller
      alwaysShowArrows={true}
      carousel={false}
      items={quickViewItems}
      itemWidth={width}
      initIndex={initIndex}
      width={width}
      disableControl={quickViewItems.length === 1}
      actions={actions}
    />
  ) : (
    <div className="data-only-header"></div>
  );
};

export default React.memo(EventThumbnails);
